import { Editor } from "@tiptap/core";
import { EditorContent } from "@tiptap/react";
import { useEffect, useRef } from "react";

import { LinkMenu } from "@/components/menus";

import { useBlockEditor } from "@/hooks/useBlockEditor";

import "../../globals.css";

import "@fontsource/inter/100.css";
import "@fontsource/inter/200.css";
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";

import "@/styles/index.css";

import ImageBlockMenu from "@/extensions/ImageBlock/components/ImageBlockMenu";
import { ColumnsMenu } from "@/extensions/MultiColumn/menus";
import { TableColumnMenu, TableRowMenu } from "@/extensions/Table/menus";
import { StepperItemMenu } from "features/editor/extensions/Stepper/menus/StepperItemMenu";
import { Control, useController } from "react-hook-form";
import { KeyboardShortcutsHelp } from "../KeyboardShortcutsHelp/KeyboardShortcutsHelp";
import { ContentItemMenu } from "../menus/ContentItemMenu";
import { TextMenu } from "../menus/TextMenu";

interface BlockEditorProps {
  name: string;
  control: Control;
  customExtensions?: [];
}

export const BlockEditor = ({
  name,
  control,
  customExtensions,
}: BlockEditorProps) => {
  const menuContainerRef = useRef(null);

  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
  });

  const { editor } = useBlockEditor(value, customExtensions);

  useEffect(() => {
    if (onChange) {
      const handleUpdate = ({ editor }: { editor: Editor }) => {
        onChange(!editor.isEmpty ? editor.getJSON() : null);
      };

      editor.on("update", handleUpdate);
      return () => {
        editor.off("update", handleUpdate);
      };
    }
  }, [editor, onChange]);

  if (!editor) {
    return null;
  }

  return (
    <div className="flex h-full" ref={menuContainerRef}>
      <div className="relative flex flex-col flex-1 h-full">
        <div className="tiptap flex items-center justify-end p-2 w-full max-w-[720px] mx-auto mt-[-30px]">
          <KeyboardShortcutsHelp />
        </div>
        <EditorContent editor={editor} className="flex-1 overflow-y-auto" />
        <ContentItemMenu editor={editor} />
        <LinkMenu editor={editor} appendTo={menuContainerRef} />
        <TextMenu editor={editor} />
        <ColumnsMenu editor={editor} appendTo={menuContainerRef} />
        <TableRowMenu editor={editor} appendTo={menuContainerRef} />
        <TableColumnMenu editor={editor} appendTo={menuContainerRef} />
        <ImageBlockMenu editor={editor} appendTo={menuContainerRef} />
        <StepperItemMenu editor={editor} appendTo={menuContainerRef} />
      </div>
    </div>
  );
};

export default BlockEditor;
